<template>
    <div>
        <div v-show="errorMessage.length > 0">
            <div class="text-red-600">
                {{ errorMessage }}
            </div>
            <br/>
            <router-link :to="{ name: 'database_connections' }" style="color:red;">
                Essayez de connecter votre compte Google à nouveau.
            </router-link>
            <hr/>
        </div>

        <div v-show="promisePending && errorMessage.length === 0" class="animate-spin flex justify-center items-center">
            <i class=" fas fa-spinner w-6 h-6"></i>
        </div>
        
        <div v-if="googleAccounts.length > 0">
            <!-- nom du credential airtable -->
            <label for="google_credential">Compte Google</label>
            <b-form-select
                id="google_credential"
                v-model="credentialName" :options="googleAccounts"
                class="mb-2">
            </b-form-select>
        </div>

        <Sheets_import_form
            v-if="googleFilesSelect.length > 0"
            :credential-name="credentialName"
            :google-files-select="googleFilesSelect"
            :file-sheets="fileSheets"
            :autocomplete-drive-files="autocompleteDriveFiles"

            :hide-import-options="true"

            @reloadSheets="(selectedFile) => loadSheets(selectedFile)"

            @returnRaw="(selectedFile, range) => getGsheetData(selectedFile, range)"
            >
        </Sheets_import_form>

        <hr/>

        <edit_tabulator
            v-show="dataLoaded"
            :extern_file="false"
            @data="() => {}"
            @update="() => {}"
            @add_row="() => {}"
            @delete_row="() => {}"
            @update_row="() => {}"
            ref="sheet_data">
        </edit_tabulator>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import google_service from "../../services/google.js";
import Sheets_import_form from '../connection_modals/sheets_import_form.vue';
import documentService from "../../services/document";
import edit_tabulator from "../edit_tabulator.vue";

const credentialName = ref("");

const errorMessage = ref("");
const promisePending = ref(false);

const googleAccounts = ref([]);
const googleFilesSelect = ref([]);
const fileSheets = ref([]);

const driveFileSearch = ref("");
const searchTimeout = ref(null);

const dataLoaded = ref(false);
const sheet_data = ref();

const emits = defineEmits(["validated"]);

const loadAccounts = async () => {
    // loadspinner
    errorMessage.value = "";
    promisePending.value = true;

    let accounts = await google_service.getAccounts()
                        .catch((err) => {
                            console.log("Error getting accounts: ", err);
                        });

    console.log("Got accounts: ", accounts);
    if (!accounts) {
        errorMessage.value = "Erreur lors de la récupération des comptes google.";
        promisePending.value = false;
        return;
    }
    accounts = accounts.googleAccounts;

    googleAccounts.value = accounts.map((account) => {
        return {
            value: account.name,
            text: account.name
        }
    });

    credentialName.value = googleAccounts.value[0].value;

    promisePending.value = false;
}

const loadFileNames = async () => {
    // loadspinner
    errorMessage.value = "";
    promisePending.value = true;

    let full_credential = credentialName.value;

    console.log("API searching wityh value: ", driveFileSearch.value);
    let files = await google_service.getDriveFiles("application/vnd.google-apps.spreadsheet", full_credential, driveFileSearch.value) 
                        .catch((err) => {
                            console.log("Error getting files: ", err);
                        });

    console.log("Got files: ", files);
    if (!files) {
        errorMessage.value = "Erreur lors de la récupération des fichiers Drive.";
        promisePending.value = false;
        return;
    }
    files = files.files;

    if (files.length === 0) {
        errorMessage.value = "Aucun fichier Google Sheets trouvé.";
        promisePending.value = false;
        return;
    }

    googleFilesSelect.value = files.map((file) => {
        return {
            value: file.id,
            text: file.name
        }
    });
    // driveFileSearch.value = googleFilesSelect.value[0].text;

    await loadSheets(googleFilesSelect.value[0].text);

    promisePending.value = false;
}

const loadSheets = async (selectedFile) => {
    // loadspinner
    errorMessage.value = "";
    promisePending.value = true;

    let full_credential = credentialName.value;

    let selectedFileObj = googleFilesSelect.value.find((file) => {
        return file.text === selectedFile;
    });

    let sheets = await google_service.getSheets(selectedFileObj.value, full_credential) 
                        .catch((err) => {
                            console.log("Error getting sheets: ", err);
                        });

    console.log("Got sheets: ", sheets);
    if (!sheets) {
        errorMessage.value = "Erreur lors de la récupération des feuilles.";
        promisePending.value = false;
        return;
    }
    sheets = sheets.sheets;

    fileSheets.value = sheets.map((sheet) => {
        return {
            value: sheet.title,
            text: sheet.title
        }
    });

    promisePending.value = false;
}

const autocompleteDriveFiles = async (search) => {

    if (search === driveFileSearch.value) {
        // return existing results
        let filteredSelect = googleFilesSelect.value.filter((file) => {
            return file.text.toLowerCase().includes(driveFileSearch.value.toLowerCase());
        });
        return filteredSelect.map((file) => {
            return file.text;
        });
    }
    driveFileSearch.value = search;

    return new Promise(resolve => {
        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value);
        }
    
        searchTimeout.value = setTimeout(async () => {
            console.log("Searching for: ", search);
            driveFileSearch.value = search;
            await loadFileNames();
            searchTimeout.value = null;
    
            let filteredSelect = googleFilesSelect.value.filter((file) => {
                return file.text.toLowerCase().includes(search.toLowerCase());
            });
        
            let results = filteredSelect.map((file) => {
                return file.text;
            });
        
            console.log("Search results: ", results)
        
            resolve(results);
        }, 500);
    });
}

const getGsheetData = async (selectedFile, range) => {

    console.log("Getting initial data for file: ", selectedFile);

    errorMessage.value = "";
    promisePending.value = true;

    let full_credential = credentialName.value;

    let selectedFileObj = googleFilesSelect.value.find((file) => {
        return file.text === selectedFile;
    });

    let data = await google_service.get_sheet_values(selectedFileObj.value, full_credential, range) 
                        .catch((err) => {
                            console.log("Error getting data: ", err);
                        });

    console.log("Got data: ", data);
    if (!data) {
        errorMessage.value = "Erreur lors de la récupération des données.";
        promisePending.value = false;
        return;
    }

    let dataLines = data.lines;

    sheet_data.value.load(dataLines);

    dataLoaded.value = true;

    promisePending.value = false;
}

watch(credentialName, async (newVal, oldVal) => {
    driveFileSearch.value = "";
    await loadFileNames();
    // await loadSheets();
});

onMounted(async () => {
    await loadAccounts();
    // await loadFileNames();
    // await loadSheets();
});

const get_data = () => {
    return sheet_data.value.get_data();
}

const get_data_files = () => {
    return sheet_data.value.get_data_files();
}

const get_data_blob = () => {
    return sheet_data.value.get_data_blob();
}

const save = () => {
    return sheet_data.value.save();
}

const load = (data) => {
    sheet_data.value.load(data);
}

const init = (data) => {
    sheet_data.value.init(data);
}

const set_data = (data) => {
    sheet_data.value.set_data(data);
}

defineExpose({
    get_data,
    get_data_files,
    save,
    load,
    init,
    set_data,
    get_data_blob
});

</script>
